import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ContactDto, ContactRequestDto } from 'src/api/FiretrolApiClient';
import { contactApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { invalidateFor } from 'src/api/queries/invalidationMap';

// `contact.save`/`contact.delete` invalidate `organization.contacts()`, which is
// a key prefix of every `contactsByCustomer` list.
export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: ContactRequestDto) =>
      contactApiClient.create(request),
    meta: { suppressErrorToast: true },
    onSettled: () => invalidateFor('contact.save', qc),
  });
}

export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: ContactRequestDto }) =>
      contactApiClient.update(id, request),
    meta: { suppressErrorToast: true },
    onSettled: () => invalidateFor('contact.save', qc),
  });
}

export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => contactApiClient.delete(id),
    meta: { successMessage: 'Contact successfully deleted!' },
    onMutate: async (id: string) => {
      const queryKey = queryKeys.organization.contacts();
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<ContactDto[]>(queryKey);
      qc.setQueryData<ContactDto[]>(queryKey, (old = []) =>
        old.filter((c) => c.id !== id),
      );
      return { previous, queryKey };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) qc.setQueryData(ctx.queryKey, ctx.previous);
    },
    onSettled: () => invalidateFor('contact.delete', qc),
  });
}
